import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiTruck, FiMapPin, FiShield, FiBarChart2,
  FiArrowRight, FiCheckCircle, FiClock, FiUsers
} from 'react-icons/fi';
import { FaWarehouse, FaIndustry } from 'react-icons/fa';
import { MdStorefront, MdSupervisorAccount } from 'react-icons/md';

const steps = [
  { icon: <FaIndustry size={30} />, label: 'Manufacturer', sublabel: 'Aachi Masala', color: '#0F4C81', bg: '#e8f1fb', step: 1 },
  { icon: <FaWarehouse size={30} />, label: 'Avivan Enterprises', sublabel: 'Distribution Hub', color: '#F39C12', bg: '#fef5e7', step: 2, highlight: true },
  { icon: <MdSupervisorAccount size={30} />, label: 'Sub-Distributor', sublabel: 'Area Coverage', color: '#9b59b6', bg: '#f5eef8', step: 3 },
  { icon: <MdStorefront size={30} />, label: 'Retailer', sublabel: 'Point of Sale', color: '#28A745', bg: '#d4edda', step: 4 },
  { icon: <FiUsers size={30} />, label: 'Customer', sublabel: 'End Consumer', color: '#dc3545', bg: '#fce4ec', step: 5 },
];

const features = [
  { icon: <FaWarehouse size={22} />, title: 'Modern Warehousing', desc: 'Temperature-controlled, organized warehouses with digital inventory management to ensure product freshness and zero stock-outs.' },
  { icon: <FiTruck size={22} />, title: 'Dedicated Fleet', desc: 'Our own delivery fleet ensures timely and safe transportation of products to all partner outlets across our distribution territory.' },
  { icon: <FiBarChart2 size={22} />, title: 'Inventory Management', desc: 'Real-time inventory tracking with automated reorder points, ensuring optimal stock levels and zero wastage.' },
  { icon: <FiShield size={22} />, title: 'Quality Control', desc: 'Stringent quality checks at every stage of the supply chain to ensure only authentic, fresh products reach our partners.' },
  { icon: <FiMapPin size={22} />, title: 'Pan-Region Coverage', desc: 'Expanding geographic coverage across Tamil Nadu and neighboring states with strategic hub-and-spoke distribution model.' },
  { icon: <FiClock size={22} />, title: '24-Hour Turnaround', desc: 'Same-day or next-day order processing and delivery within our primary service zones for urgent requirements.' },
];

const coverageAreas = [
  { state: 'Tamil Nadu', districts: ['Chennai', 'Coimbatore', 'Madurai', 'Trichy', 'Salem', 'Erode', 'Tirunelveli', 'Vellore', 'Thanjavur', 'Dindigul'], status: 'active' },
  { state: 'Karnataka', districts: ['Bengaluru', 'Mysuru', 'Hubli', 'Mangaluru'], status: 'expanding' },
  { state: 'Kerala', districts: ['Thiruvananthapuram', 'Kochi', 'Kozhikode'], status: 'upcoming' },
  { state: 'Andhra Pradesh', districts: ['Hyderabad', 'Visakhapatnam', 'Vijayawada'], status: 'upcoming' },
];

export default function DistributionNetwork() {
  return (
    <>
      <Helmet>
        <title>Distribution Network | Avivan Enterprises — FMCG Supply Chain India</title>
        <meta name="description" content="Learn about Avivan Enterprises' FMCG distribution network, supply chain process, logistics, and coverage areas across India." />
      </Helmet>

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="page-banner-content text-center">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(243,156,18,0.2)', color: '#f39c12', padding: '6px 18px', borderRadius: 50, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, border: '1px solid rgba(243,156,18,0.3)' }}>
              🚚 Supply Chain Excellence
            </div>
            <h1>Distribution Network</h1>
            <p>A robust, scalable supply chain built for reliable FMCG distribution across India</p>
            <div className="breadcrumb-c" style={{ justifyContent: 'center' }}>
              <Link to="/">Home</Link>
              <span className="sep">/</span>
              <span style={{ color: 'var(--accent)' }}>Distribution Network</span>
            </div>
          </div>
        </div>
      </div>

      {/* Distribution Process */}
      <section style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">🔄 How It Works</span>
            <h2 className="section-title">Our Distribution <span>Process</span></h2>
            <p className="section-subtitle">A seamless 5-step supply chain ensuring products reach consumers in perfect condition</p>
            <div className="divider-bar" />
          </div>

          {/* Flow Diagram */}
          <div style={{ background: 'var(--light)', borderRadius: 20, padding: '48px 32px', marginBottom: 60 }} data-aos="fade-up" data-aos-delay="100">
            <div className="process-flow">
              {steps.map((s, i) => (
                <React.Fragment key={s.step}>
                  <div className="process-step">
                    <div className={`process-icon ${s.highlight ? 'float' : ''}`} style={{
                      background: s.highlight ? 'var(--gradient-accent)' : s.bg,
                      border: s.highlight ? '3px solid var(--accent)' : `2px solid ${s.color}22`,
                      transform: s.highlight ? 'scale(1.15)' : 'scale(1)',
                    }}>
                      <span style={{ color: s.highlight ? '#fff' : s.color }}>{s.icon}</span>
                    </div>
                    {s.highlight && (
                      <div style={{ position: 'absolute', top: -8, right: -8, background: 'var(--secondary)', color: '#fff', fontSize: '0.58rem', fontWeight: 800, padding: '3px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '1px' }}>You</div>
                    )}
                    <div className="process-label" style={{ color: s.highlight ? 'var(--accent-dark)' : 'var(--dark)', fontWeight: s.highlight ? 800 : 700 }}>{s.label}</div>
                    <div className="process-sublabel">{s.sublabel}</div>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="process-arrow">
                      <FiArrowRight style={{ color: 'var(--primary)', fontSize: '1.8rem', flexShrink: 0 }} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Process Details */}
          <div className="row g-4">
            {[
              { step: '01', title: 'Procurement', desc: 'We source directly from manufacturers like Aachi Masala, ensuring authenticity, freshness, and competitive pricing for our distribution network.', icon: '🏭' },
              { step: '02', title: 'Quality Verification', desc: 'Every batch is quality-checked upon receipt — batch numbers, expiry dates, packaging integrity, and product quality are verified before storage.', icon: '✅' },
              { step: '03', title: 'Storage & Inventory', desc: 'Products are stored in our organized warehouse with proper category segregation, FIFO management, and real-time digital inventory tracking.', icon: '📦' },
              { step: '04', title: 'Order Processing', desc: 'Orders from dealers, retailers, and institutions are processed within 2-4 hours. Automated alerts ensure no order is missed or delayed.', icon: '📋' },
              { step: '05', title: 'Delivery', desc: 'Our dedicated logistics team ensures safe and timely delivery to all partner outlets. Same-day delivery available within primary zones.', icon: '🚚' },
              { step: '06', title: 'After-Sales Support', desc: 'Post-delivery, our team follows up to ensure satisfaction, handles returns, and collects feedback to improve our service continuously.', icon: '🤝' },
            ].map((s, i) => (
              <div className="col-md-6 col-lg-4" key={s.step} data-aos="fade-up" data-aos-delay={i * 60}>
                <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: 28, height: '100%', transition: 'var(--transition)' }} className="card-hover">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                    <div style={{ width: 48, height: 48, background: 'var(--primary-xlight)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>
                      {s.icon}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>Step {s.step}</div>
                  </div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 10 }}>{s.title}</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure Features */}
      <section style={{ padding: '80px 0', background: 'var(--light)' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge"><FaWarehouse size={12} /> Our Infrastructure</span>
            <h2 className="section-title">Logistics & Supply <span>Chain Excellence</span></h2>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            {features.map((f, i) => (
              <div className="col-md-6 col-lg-4" key={f.title} data-aos="fade-up" data-aos-delay={i * 60}>
                <div className="why-card">
                  <div className="why-icon">{f.icon}</div>
                  <div className="why-text">
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge"><FiMapPin size={12} /> Service Regions</span>
            <h2 className="section-title">Coverage <span>Areas</span></h2>
            <p className="section-subtitle">Currently serving Tamil Nadu and expanding across South India</p>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            {coverageAreas.map((area, i) => {
              const statusConfig = {
                active: { bg: '#d4edda', color: '#28A745', border: '#28A74533', label: '✅ Active', tag: 'badge-active' },
                expanding: { bg: '#fef5e7', color: '#F39C12', border: '#F39C1233', label: '🚀 Expanding', tag: 'badge-coming-soon' },
                upcoming: { bg: '#e8f1fb', color: '#0F4C81', border: '#0F4C8133', label: '📅 Upcoming', tag: '' },
              }[area.status];
              return (
                <div className="col-lg-3 col-md-6" key={area.state} data-aos="fade-up" data-aos-delay={i * 80}>
                  <div style={{ background: '#fff', border: `1.5px solid ${statusConfig.border}`, borderRadius: 16, padding: 24, height: '100%' }} className="card-hover">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <h4 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0, color: 'var(--dark)' }}>{area.state}</h4>
                      <span style={{ background: statusConfig.bg, color: statusConfig.color, padding: '4px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {area.districts.map(d => (
                        <span key={d} style={{ background: 'var(--light)', color: 'var(--text-muted)', padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 500 }}>
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Dealer Support */}
      <section style={{ padding: '80px 0', background: 'var(--gradient-primary)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 300, height: 300, background: 'rgba(255,255,255,0.04)', borderRadius: '50%' }} />
        <div className="container">
          <div className="row align-items-center" data-aos="fade-up">
            <div className="col-lg-7">
              <h2 style={{ color: '#fff', fontWeight: 900, fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)', marginBottom: 16 }}>
                Dealer Support & Partnership
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, marginBottom: 24 }}>
                Our dealer support goes beyond just product supply. We provide comprehensive business support to help our partners grow, including market insights, promotional materials, training, and a dedicated account manager.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 32 }}>
                {[
                  'Dedicated Account Manager',
                  'Marketing & POS Materials',
                  'Sales Training Support',
                  'Flexible Payment Terms',
                  'Promotional Campaign Support',
                  'Regular Business Reviews',
                ].map(s => (
                  <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem' }}>
                    <FiCheckCircle style={{ color: 'var(--accent)', flexShrink: 0 }} />
                    {s}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <Link to="/become-dealer" className="btn-accent-c">
                  Apply for Dealership <FiArrowRight size={15} />
                </Link>
                <Link to="/contact" className="btn-outline-white">
                  Talk to Our Team
                </Link>
              </div>
            </div>
            <div className="col-lg-5 d-none d-lg-block text-center" data-aos="fade-left" data-aos-delay="200">
              <div style={{ fontSize: '8rem' }}>🚚</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginTop: 8 }}>
                Fast & Reliable Distribution
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
